import type { ToolInput } from '@/features/events/shared'
import {
  formatMcpToolForDisplay,
  formatToolNameForDisplay,
  getFileNameFromPath,
  isBashToolInput,
  isEditToolInput,
  isGlobToolInput,
  isGrepToolInput,
  isReadToolInput,
  isWebFetchToolInput,
  isWebSearchToolInput,
  isWriteToolInput,
  parseMcpToolName,
} from '@/features/events/shared'
import { ERRORED_CONTEXT_SUFFIX, ERRORED_NEEDS_REVIEW } from '../constants'

const INTENT_SEPARATOR = ' · '

/**
 * Structured tool intent so the inbox can render the tool name as a plain label
 * and the intent detail in a popping blue. `detail` is empty when the tool input
 * carries no meaningful argument (the tool name then stands on its own).
 */
export interface ToolIntent {
  readonly toolName: string
  readonly detail: string
}

export function buildToolIntent(toolName: string, toolInput: ToolInput): ToolIntent {
  if (parseMcpToolName(toolName).isMcp) {
    const formatted = formatMcpToolForDisplay(toolName)
    if (formatted) {
      return { toolName: formatted.toolDisplayName, detail: formatted.actionDisplayName }
    }
  }

  if (isBashToolInput(toolInput)) {
    return { toolName: 'Bash', detail: toolInput.description ?? toolInput.command }
  }

  if (isReadToolInput(toolInput)) {
    return { toolName: 'Read', detail: getFileNameFromPath(toolInput.file_path) }
  }

  if (isEditToolInput(toolInput)) {
    return { toolName: 'Edit', detail: getFileNameFromPath(toolInput.file_path) }
  }

  if (isWriteToolInput(toolInput)) {
    return { toolName: 'Write', detail: getFileNameFromPath(toolInput.file_path) }
  }

  if (isGrepToolInput(toolInput)) {
    return { toolName: 'Grep', detail: toolInput.pattern }
  }

  if (isGlobToolInput(toolInput)) {
    return { toolName: 'Glob', detail: toolInput.pattern }
  }

  if (isWebFetchToolInput(toolInput)) {
    return { toolName: 'WebFetch', detail: toolInput.url }
  }

  if (isWebSearchToolInput(toolInput)) {
    return { toolName: 'WebSearch', detail: toolInput.query }
  }

  return { toolName: formatToolNameForDisplay(toolName), detail: '' }
}

export function joinToolIntent(intent: ToolIntent): string {
  return intent.detail ? `${intent.toolName}${INTENT_SEPARATOR}${intent.detail}` : intent.toolName
}

/**
 * Builds the errored "needs review" description line shown beneath the tool name.
 * Surviving tool context gets the review suffix appended; a missing detail falls
 * back to the standalone needs-review label.
 */
export function formatErroredDescriptionLine(detail: string | undefined): string {
  return detail ? `${detail}${ERRORED_CONTEXT_SUFFIX}` : ERRORED_NEEDS_REVIEW
}
